import { JetView } from 'webix-jet'; // Importing JetView from webix-jet framework
import { getLangsList } from 'models/langslist'; // Importing function to get language list
import 'locales/webix/de.js'; // Importing German locale
import 'locales/webix/es.js'; // Importing Spanish locale
import 'locales/webix/ko.js'; // Importing Korean locale
import 'locales/webix/ru.js'; // Importing Russian locale
import 'locales/webix/zh.js'; // Importing Chinese locale
import { devConfig, profile } from 'config/config'; // Importing development configuration and user profile

/**
 * SettingsView class extends JetView to create a settings interface.
 */
export default class SettingsView extends JetView {
    /**
     * Toggles the application language based on the selected value.
     * @param {string} value - The language code to set.
     */
    toggleLanguage(value) {
        const langs = this.app.getService('locale'); // Get the locale service
        if (value != langs.getLang()) {
            // Check if the selected language is different
            langs.setLang(value); // Set the new language
        }
    }

    /**
     * Configures the UI components of the settings view.
     * @returns {Object} - The configuration object for the view.
     */
    config() {
        const _ = this.app.getService('locale')._; // Localization function
        const lang = this.app.getService('locale').getLang(); // Current language
        const theme = this.app.config.theme; // Current theme
        const combo_theme_value = theme ? '1' : '0'; // Theme value for combo
        const date_combo_value = this.app.config.dateFormat; // Date format
        const list_length_slider_value = this.app.config.listLength; // List length
        webix.ui.fullScreen(); // Set the UI to full screen
        const vthis = this; // Preserve context for use in callbacks

        return {
            id: 'a5', // Unique ID for the form
            type: 'clean', // Clean type for the form
            view: 'form', // Form view
            scroll: 'y', // Enable vertical scrolling
            rows: [
                {
                    template: _('Settings'), // Header template with localization
                    type: 'header', // Header type
                    css: `webix_header ${theme}`, // CSS class for header
                },
                {
                    view: 'form', // Nested form view
                    elementsConfig: { labelPosition: 'top' }, // Label position configuration
                    rules: {
                        $all: webix.rules.isNotEmpty, // Validation rule to ensure fields are not empty
                    },
                    elements: [
                        { template: _('Regional settings'), type: 'section' }, // Section header
                        {
                            id: 'b2', // Unique ID for the container
                            rows: [
                                {
                                    margin: 10, // Margin around the container
                                    responsive: 'b2', // Responsive behavior
                                    cols: [
                                        {
                                            label: _('Zona horaria:'), // Label for timezone selection
                                            view: 'richselect', // Rich select view
                                            id: 'TimeZone', // Unique ID for timezone select
                                            name: 'TimeZone', // Name for form submission
                                            localId: 'TimeZone:combo', // Local ID for reference
                                            value: profile.Settings.TimeZone, // Default value from profile
                                            gravity: 3, // Gravity for layout
                                            minWidth: 160, // Minimum width for the select
                                            options: [], // Options will be populated dynamically
                                            on: {
                                                onChange: function (newlang) {
                                                    // Event handler for language change
                                                    this.$scope._lang = newlang; // Store selected language
                                                    const country = this.getList().getItem(newlang).code; // Get country code
                                                    webix.i18n.setLocale(
                                                        newlang + '-' + country, // Set locale
                                                    );
                                                },
                                            },
                                        },
                                        {
                                            label: _('Moneda para reportes'), // Label for report currency selection
                                            view: 'richselect', // Rich select view
                                            id: 'ReportCurrency', // Unique ID for currency select
                                            name: 'ReportCurrency', // Name for form submission
                                            localId: 'ReportCurrency:combo', // Local ID for reference
                                            value: profile.Settings.ReportCurrency, // Default value from profile
                                            gravity: 3, // Gravity for layout
                                            minWidth: 160, // Minimum width for the select
                                            options: [], // Options will be populated dynamically
                                            on: {},
                                        },
                                        {
                                            label: _('Language'), // Label for language selection
                                            view: 'richselect', // Rich select view
                                            name: 'lang', // Name for form submission
                                            localId: 'langs:combo', // Local ID for reference
                                            value: lang, // Default value from locale service
                                            gravity: 3, // Gravity for layout
                                            minWidth: 160, // Minimum width for the select
                                            options: getLangsList(), // Populate options with available languages
                                        },
                                    ],
                                },
                            ],
                        },
                        {
                            template: _('Configuración Partner'), // Section header for partner settings
                            type: 'section', // Section type
                        },
                        {
                            id: 'b1', // Unique ID for the container
                            rows: [
                                {
                                    margin: 10, // Margin around the container
                                    responsive: 'b1', // Responsive behavior
                                    hidden: !(profile.Partners.length > 2), // Hide if less than 3 partners
                                    cols: [
                                        {
                                            label: _('Partner'), // Label for partner selection
                                            id: 'Partners2', // Unique ID for partner select
                                            view: 'richselect', // Rich select view
                                            name: 'Partner', // Name for form submission
                                            gravity: 3, // Gravity for layout
                                            minWidth: 160, // Minimum width for the select
                                            placeholder: _(
                                                'Click para seleccionar ', // Placeholder text
                                            ),
                                            value: profile.PartnerSelected, // Default value from profile
                                            options: profile.Partners, // Options from profile
                                        },
                                    ],
                                },
                            ],
                        },
                        {
                            template: _('Afiliados'), // Section header for affiliates
                            type: 'section', // Section type
                            hidden: !devConfig.getPermAction(
                                'linkAffiliate',
                                'Add', // Hide if no permission to add affiliate links
                            ),
                            css: 'prm-linkAffiliate', // CSS class for styling
                        },
                        {
                            id: 'linkAffiliate2', // Unique ID for affiliate link container
                            hidden: !devConfig.getPermAction(
                                'linkAffiliate',
                                'Add', // Hide if no permission to add affiliate links
                            ),
                            css: 'prm-linkAffiliate', // CSS class for styling
                            cols: [
                                {
                                    view: 'icon', // Icon view for copy action
                                    icon: 'mdi mdi-content-copy', // Icon class
                                    click: function () {
                                        // Click event to copy link
                                        webix.message('Link Copiado'); // Show message
                                        navigator.clipboard.writeText(
                                            $$('linkAf').getValue(), // Copy link value to clipboard
                                        );
                                    },
                                },
                                {
                                    id: 'linkAffiliate', // Unique ID for affiliate link input
                                    css: 'saldos', // CSS class for styling
                                    padding: { right: 15 }, // Padding for layout
                                    rows: [
                                        {
                                            height: 50, // Height of the label
                                            view: 'label', // Label view
                                            label: _('Link Afiliados:'), // Label text
                                            margin: 0, // Margin around the label
                                            css: 'title-affiliate', // CSS class for styling
                                        },
                                        {
                                            css: 'col-link-af', // CSS class for layout
                                            cols: [
                                                {
                                                    css: 'linkAf', // CSS class for input
                                                    height: 35, // Height of the input
                                                    view: 'text', // Text input view
                                                    id: 'linkAf', // Unique ID for input
                                                    value: profile.linkAffiliate, // Default value from profile
                                                    readonly: true, // Make input read-only
                                                    click: function () {
                                                        // Click event to copy link
                                                        webix.message(
                                                            'Link Copiado', // Show message
                                                        );
                                                        navigator.clipboard.writeText(
                                                            $$('linkAf').getValue(), // Copy link value to clipboard
                                                        );
                                                    },
                                                },
                                            ],
                                        },
                                    ],
                                },
                            ],
                        },
                        {
                            id: 'linkAffiliateQR', // Unique ID for QR link container
                            hidden: !devConfig.getPermAction(
                                'linkAffiliateQR',
                                'Add', // Hide if no permission to add QR affiliate links
                            ),
                            css: 'prm-linkAffiliateQR', // CSS class for styling
                            cols: [
                                {
                                    view: 'icon', // Icon view for QR action
                                    icon: 'mdi mdi-qrcode-scan', // Icon class
                                    click: () => {
                                        // Click event to open QR modal
                                        this.app.callEvent('qrModal', [{}]); // Call event to show QR modal
                                    },
                                },
                                {
                                    id: 'linkAffiliateQ', // Unique ID for QR link input
                                    css: 'saldos', // CSS class for styling
                                    padding: { right: 15 }, // Padding for layout
                                    rows: [
                                        {
                                            height: 50, // Height of the label
                                            view: 'label', // Label view
                                            label: _('QR Afiliados:'), // Label text
                                            margin: 0, // Margin around the label
                                            css: 'title-affiliate', // CSS class for styling
                                        },
                                        {
                                            css: 'col-link-af', // CSS class for layout
                                            cols: [
                                                {
                                                    css: 'linkAf', // CSS class for input
                                                    height: 35, // Height of the input
                                                    view: 'text', // Text input view
                                                    id: 'linkAfQR', // Unique ID for input
                                                    value: _('Ver QR'), // Default value for QR link
                                                    readonly: true, // Make input read-only
                                                    click: () => {
                                                        // Click event to open QR modal
                                                        this.app.callEvent(
                                                            'qrModal',
                                                            [{}], // Call event to show QR modal
                                                        );
                                                    },
                                                },
                                            ],
                                        },
                                    ],
                                },
                            ],
                        },
                        { template: '-', type: 'section', css: 'fixBorder' }, // Separator section
                        {},
                        {
                            margin: 10, // Margin around the button container
                            cols: [
                                {},
                                {
                                    view: 'button', // Button view for saving settings
                                    value: _('Save'), // Button text
                                    autowidth: true, // Auto width for button
                                    type: 'form', // Button type
                                    click: function () {
                                        // Click event to save settings
                                        var Partners2 = $$('Partners2').getValue(); // Get selected partner value
                                        if (Partners2 == '') {
                                            // Check if no partner selected
                                            Partners2 = 0; // Default to 0
                                        }
                                        var cthis = this; // Preserve context for use in callbacks
                                        var PartnerSelected = Partners2; // Store selected partner
                                        webix
                                            .ajax() // AJAX request to save partner
                                            .headers({
                                                'Content-type': 'application/json', // Set content type
                                            })
                                            .post(
                                                devConfig.geturlApi() + 'setting/SavePartnerUser', // API endpoint
                                                '{ "PartnerSelected":' + PartnerSelected + '}', // Request body
                                                {
                                                    error: function (text, data, XmlHttpRequest) {
                                                        webix.message(
                                                            'Error! ', // Show error message
                                                        );
                                                    },
                                                    success: function (text, data, XmlHttpRequest) {
                                                        var json = JSON.parse(text); // Parse response
                                                        if (json.HasError) {
                                                            webix.message(
                                                                'Error! ', // Show error message
                                                            );
                                                        } else {
                                                            console.log(
                                                                devConfig.getPartnerSelected(), // Log selected partner
                                                            );
                                                        }
                                                    },
                                                },
                                            );
                                        const newdata = this.getFormView().getValues(); // Get form values
                                        var cthis = this; // Preserve context for use in callbacks
                                        webix
                                            .ajax() // AJAX request to save settings
                                            .headers({
                                                'Content-type': 'application/json', // Set content type
                                            })
                                            .post(
                                                devConfig.geturlApi() + 'setting/saveSetting2', // API endpoint
                                                newdata, // Request body
                                                {
                                                    error: function (text, data, XmlHttpRequest) {
                                                        webix.message(
                                                            'Error! ', // Show error message
                                                        );
                                                    },
                                                    success: function (text, data, XmlHttpRequest) {
                                                        var json = JSON.parse(text); // Parse response
                                                        if (json.HasError) {
                                                            webix.message(
                                                                'Error! ', // Show error message
                                                            );
                                                        } else {
                                                            devConfig.setTimezone(
                                                                newdata.TimeZone, // Update timezone in config
                                                            );
                                                            devConfig.setCurrencyReport(
                                                                newdata.ReportCurrency, // Update currency in config
                                                            );
                                                            if (
                                                                newdata.lang != undefined // Check if language is defined
                                                            ) {
                                                                vthis.toggleLanguage(
                                                                    newdata.lang.toLowerCase(), // Toggle language
                                                                );
                                                            }
                                                            webix.message(
                                                                'Los datos fueron actualizados satisfactoriamente.', // Show success message
                                                            );
                                                            window.location.reload(); // Reload the page
                                                        }
                                                    },
                                                },
                                            );
                                    },
                                },
                            ],
                        },
                    ],
                },
            ],
        };
    }

    /**
     * Initializes the settings view and fetches initial settings data.
     */
    init() {
        this._lang = this.app.getService('locale').getLang(); // Get current language
        this._defaults = {
            lang: 'es', // Default language
            dateformat: '%j %F, %H:%i', // Default date format
            moneyformat: '1', // Default money format
            theme: '0', // Default theme
            maxlist: 50, // Maximum list length
        };
        webix
            .ajax() // AJAX request to fetch settings
            .headers({
                'Content-type': 'application/json', // Set content type
            })
            .post(devConfig.geturlApi() + 'setting/GetSetting', '', {
                error: function (text, data, XmlHttpRequest) {
                    webix.message('Error! '); // Show error message
                },
                success: function (text, data, XmlHttpRequest) {
                    var json = JSON.parse(text); // Parse response
                    if (json.HasError) {
                        webix.message('Error! '); // Show error message
                    } else {
                        var data = JSON.parse(
                            text
                                .replace(/"Id"/g, '"id"') // Normalize ID field
                                .replace(/"Name"/g, '"value"') // Normalize Name field
                                .replace(/"DisplayName"/g, '"value"'), // Normalize DisplayName field
                        );
                        var optionsTimezone = $$('TimeZone').getList(); // Get timezone options
                        console.log(data); // Log fetched data
                        optionsTimezone.clearAll(); // Clear existing options
                        optionsTimezone.parse(data.Data.TimeZones); // Populate timezone options
                        var optionsReportCurrency = $$('ReportCurrency').getList(); // Get report currency options
                        optionsReportCurrency.clearAll(); // Clear existing options
                        optionsReportCurrency.parse(data.Data.ReportCurrencies); // Populate report currency options
                    }
                },
            });
    }
}

// FILE DOCUMENTED
